import { getAccountMoney } from '../../../shared/money/exact-money.js';
import { getProfileStatus, isPendingVerificationAccount } from '../model/account-platform-state.js';

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeAccounts(payload) {
  if (Array.isArray(payload?.accounts)) {
    return payload.accounts;
  }

  if (payload?.accounts && typeof payload.accounts === 'object') {
    return Object.values(payload.accounts);
  }

  return normalizeList(payload);
}

export const ACCOUNT_SORT_FIELDS = Object.freeze(['created', 'modified', 'login', 'leverage']);
export const ACCOUNT_FILTER_FIELDS = Object.freeze(['login', 'type', 'platform', 'currency', 'server']);
export const DEFAULT_ACCOUNT_SORT = Object.freeze({
  sortBy: 'created',
  sortOrder: 'desc',
});

function buildProfileName(account) {
  const profile = account?.profile || {};
  const firstName = pickFirst(profile.firstName, profile.first_name);
  const lastName = pickFirst(profile.lastName, profile.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(profile.email, profile.uid) || '-';
}

function extractAccountSync(account) {
  return (
    account?.sync ||
    account?.tpAccountSync ||
    account?.tpAccountSyncDTO ||
    account?.tp_account_sync ||
    account?.tp_account_sync_dto ||
    null
  );
}

function pickSyncField(sync, ...keys) {
  return pickFirst(...keys.map((key) => sync?.[key]));
}

export function buildSyncSuccessMessage(response) {
  const queueId = pickFirst(response?.queueId, response?.queue_id);
  if (queueId) {
    return `Sync queued (queueId: ${queueId})`;
  }

  return pickFirst(response?.message, response?.detail, response?.status, response?.resultMessage) || 'Account synced successfully';
}

export function buildAccountsRequestDto(pagination = {}) {
  const sortBy = ACCOUNT_SORT_FIELDS.includes(pagination?.sortBy) ? pagination.sortBy : DEFAULT_ACCOUNT_SORT.sortBy;
  const sortOrder = ['asc', 'desc'].includes(pagination?.sortOrder)
    ? pagination.sortOrder
    : DEFAULT_ACCOUNT_SORT.sortOrder;
  const dto = {
    sortBy,
    sortOrder,
    limit: Number.isInteger(pagination?.limit) && pagination.limit > 0 ? pagination.limit : 20,
  };

  if (Number.isInteger(pagination?.offset) && pagination.offset > 0) {
    dto.offset = pagination.offset;
  }

  const filters = pagination?.filters || {};
  const filterBy = ACCOUNT_FILTER_FIELDS
    .map((field) => ({
      field,
      filter: String(filters[field] || '').trim(),
    }))
    .filter((item) => item.filter);

  if (filterBy.length > 0) {
    dto.filterBy = filterBy;
  }

  return dto;
}

export function mapAccountsResponseToDto(payload) {
  return normalizeAccounts(payload).map((account, index) => {
    const sync = extractAccountSync(account);

    return {
      key: String(pickFirst(account.uid, account.id, `${pickFirst(account.login, 'account')}-${index}`)),
      accountId: String(pickFirst(account.id, account.uid) || ''),
      login: pickFirst(account.terminalLogin, account.login) || '-',
      accountNumber: pickFirst(account.accountNumber, account.login) || '-',
      type: pickFirst(account.type, account.accountType, account.typeLabel) || '-',
      platform: pickFirst(account.platform) || '-',
      currency: pickFirst(account.currency) || '-',
      leverage: pickFirst(account.leverage, pickSyncField(sync, 'leverage')) || '-',
      balance: getAccountMoney(account, 'balance'),
      equity: getAccountMoney(account, 'equity'),
      freeMargin: getAccountMoney(account, 'freeMargin'),
      usedMargin: getAccountMoney(account, 'usedMargin'),
      lastSuccessfulSyncAt: pickSyncField(sync, 'lastSuccessfulSyncAt', 'last_successful_sync_at'),
      lastSyncFreshnessAt: pickFirst(account.lastSyncFreshnessAt, account.syncedAt, account.synced_at),
      profileUid: String(pickFirst(account?.profile?.uid, account?.profile?.id) || '').trim(),
      profileStatus: getProfileStatus(account),
      profileName: buildProfileName(account),
      created: pickFirst(account.created, account.createdAt),
      isPendingVerification: isPendingVerificationAccount(account),
      source: account,
    };
  });
}
